'use client';

import { useId, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiInformationLine, RiPauseCircleLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { useInlineSign } from '@/i18n/direction';
import { dur, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';
import { AD_INTEGRATIONS } from '@/lib/site-integrations';

import './campaigns.css';

/**
 * "Bir kez anlatın" — tek brief, beş platformun kendi kurgusu.
 *
 * Solda örnek bir brief (amaç: satış). Sağda platform seçiliyor ve brief'in
 * O PLATFORMDA neye dönüştüğü çiziliyor: üç katmanlı yapı + yalnız o
 * platformun sorduğu eksik alanlar. İçerik ürünün kendisinden
 * (`@mentorivo/shared` → `campaign-brief.ts`):
 *   satış hedefi   Meta OUTCOME_SALES (+ satın alma olayı) · Google SEARCH
 *                  (PMax/Alışveriş/Demand Gen seçilebilir) · TikTok
 *                  WEB_CONVERSIONS (+ alışveriş olayı) · LinkedIn
 *                  WEBSITE_CONVERSION · X WEBSITE_CLICKS (+ dönüşüm
 *                  optimizasyonu, web olay etiketi)
 *   eksikler       `BRIEF_GAP` — Meta sayfa/piksel; Google kelime, ≥3 başlık,
 *                  ≥2 açıklama; TikTok Spark hesabı, piksel, yerleşim, ≤100
 *                  karakter metin; LinkedIn şirket sayfası, dil; X ödeme
 *                  yöntemi, web olay etiketi, ≤280 karakter gönderi
 *   durum          her şey DURAKLATILMIŞ açılır (`campaign-brief.ts` ~2692)
 * "Platform son" brief'in fikri tam bu: ortak alanlar bir kez sorulur,
 * platforma özgü olan yalnız o platform seçilince.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * • Platform değişince yapı satırları kısa kademeyle iniyor (kampanya →
 *   set → reklam: hiyerarşinin okuma sırası); panel yön biliyor.
 * • Brief'te O platformun özel ilgilendiği satırlar yanıyor (yeşil kenar +
 *   "bu platform soruyor" yazısı) — brief ile çıktı arasındaki bağ görsel.
 */

const FIELDS = ['product', 'goal', 'budget', 'audience', 'creative', 'copy'] as const;
type Field = (typeof FIELDS)[number];

type PlatformKey = (typeof AD_INTEGRATIONS)[number]['key'];

/** Platformun brief'ten özellikle baktığı / ek kural koyduğu alanlar. */
const HOT: Record<PlatformKey, readonly Field[]> = {
  meta: ['goal'],
  google: ['copy'],
  tiktok: ['creative', 'copy'],
  linkedin: ['audience'],
  x: ['copy', 'budget'],
};

const LEVELS = ['l1', 'l2', 'l3'] as const;
const GAP_COUNT: Record<PlatformKey, number> = { meta: 2, google: 3, tiktok: 4, linkedin: 2, x: 3 };

const panelV: Variants = {
  enter: (dir: number) => ({ opacity: 0, x: 18 * dir, filter: 'blur(4px)' }),
  show: {
    opacity: 1,
    x: 0,
    filter: 'blur(0px)',
    transition: { ...springFade(spring.default), staggerChildren: stagger.base, delayChildren: 0.05 },
  },
  leave: (dir: number) => ({
    opacity: 0,
    x: -12 * dir,
    filter: 'blur(3px)',
    transition: { duration: dur.instant },
  }),
};

const levelV: Variants = {
  enter: { opacity: 0, y: -6 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

export function CampaignBriefDemo() {
  const t = useTranslations('featuresCampaigns.brief');
  const uid = useId();
  const sign = useInlineSign();
  const [[active, dir], setState] = useState<[PlatformKey, number]>(['meta', 1]);
  const platform = AD_INTEGRATIONS.find((p) => p.key === active)!;
  const order = AD_INTEGRATIONS.map((p) => p.key);

  const choose = (key: PlatformKey) => {
    if (key === active) return;
    setState([key, order.indexOf(key) > order.indexOf(active) ? 1 : -1]);
  };

  return (
    <ProductSection tone="white" labelledBy={`${uid}-title`}>
      <SectionHead
        id={`${uid}-title`}
        eyebrow={t('eyebrow')}
        title={t.rich('title', { hl })}
        lead={t('lead')}
      />

      <div className="cbd">
        {/* --- Brief ---------------------------------------------------- */}
        <div className="cbd-brief">
          <p className="cbd-head">{t('briefHead')}</p>
          <dl className="cbd-fields">
            {FIELDS.map((field) => {
              const hot = HOT[active].includes(field);
              return (
                <div key={field} className="cbd-field" data-hot={hot ? '' : undefined}>
                  <dt>{t(`fields.${field}.label`)}</dt>
                  <dd>
                    {t(`fields.${field}.value`)}
                    <AnimatePresence initial={false}>
                      {hot && (
                        <motion.span
                          key={`${active}-${field}`}
                          className="cbd-hot"
                          initial={{ opacity: 0, y: 3 }}
                          animate={{ opacity: 1, y: 0, transition: springFade(spring.snap) }}
                          exit={{ opacity: 0, transition: { duration: dur.instant } }}
                        >
                          {t(`hot.${active}.${field}`)}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>

        {/* --- Platform -------------------------------------------------- */}
        <div className="cbd-out">
          <div className="cbd-tabs" role="group" aria-label={t('tabsLabel')}>
            {AD_INTEGRATIONS.map((p) => (
              <button
                key={p.key}
                type="button"
                className="cbd-tab"
                aria-pressed={active === p.key}
                aria-controls={`${uid}-panel`}
                onClick={() => choose(p.key)}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  className="cbd-tab-mark"
                  src={p.mark}
                  alt=""
                  width={p.w}
                  height={p.h}
                  loading="lazy"
                  decoding="async"
                />
                <span className="cbd-tab-name">{p.name}</span>
              </button>
            ))}
          </div>

          <div className="cbd-stage" id={`${uid}-panel`} aria-live="polite">
            <AnimatePresence mode="wait" initial={false} custom={dir * sign}>
              <motion.div
                key={active}
                className="cbd-panel"
                custom={dir * sign}
                variants={panelV}
                initial="enter"
                animate="show"
                exit="leave"
              >
                <div className="cbd-panel-top">
                  <p className="cbd-panel-name">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      className="cbd-panel-mark"
                      src={platform.mark}
                      alt=""
                      width={platform.w}
                      height={platform.h}
                      decoding="async"
                    />
                    {t(`platforms.${active}.name`)}
                  </p>
                  <span className="cbd-paused">
                    <RiPauseCircleLine size={14} aria-hidden="true" />
                    {t('paused')}
                  </span>
                </div>

                <p className="cbd-mini">{t('builds')}</p>
                <ol className="cbd-levels">
                  {LEVELS.map((level) => (
                    <motion.li key={level} className="cbd-level" variants={levelV}>
                      <span className="cbd-level-kind">{t(`platforms.${active}.${level}.kind`)}</span>
                      <span className="cbd-level-what">{t(`platforms.${active}.${level}.what`)}</span>
                    </motion.li>
                  ))}
                </ol>

                <p className="cbd-mini">{t('asks')}</p>
                <motion.ul className="cbd-gaps" variants={levelV}>
                  {Array.from({ length: GAP_COUNT[active] }, (_, i) => (
                    <li key={i} className="cbd-gap">
                      {t(`platforms.${active}.gaps.g${i + 1}`)}
                    </li>
                  ))}
                </motion.ul>

                <motion.p className="cbd-note" variants={levelV}>
                  <RiInformationLine size={15} aria-hidden="true" />
                  {t(`platforms.${active}.note`)}
                </motion.p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      <p className="ps-note">{t('foot')}</p>
    </ProductSection>
  );
}
